/** @file TreeSearch: the search box, the group 1-7 chips with counts and the badge legend above the tree. */
import type { CSSProperties, ReactElement } from 'react';
import { useState } from 'react';
import { useStore } from 'zustand';
import { useEditorStore } from '../../state/useEditorStore';
import { badgeLegend } from './badgeLegend';
import { treeUi } from './treeUi';

const GROUPS = [1, 2, 3, 4, 5, 6, 7] as const;
const chip = (on: boolean, empty: boolean): CSSProperties => ({
  fontSize: 10, padding: '0 5px', borderRadius: 8, cursor: 'pointer', color: on ? 'var(--accent, #f2b544)' : 'inherit',
  border: `1px solid ${on ? 'var(--accent, #f2b544)' : 'var(--border, #2a2e38)'}`, background: 'none', opacity: empty && !on ? 0.4 : 1,
});

/** Search grammar help shown as the input's tooltip. */
const hint = 'Name substring; g:3 group, l: / r: mirror side, d: has drawables, s: socket target; terms combine with AND. Esc clears.';

/** Reads and writes the tree UI store; `counts[n - 1]` is the number of nodes in group n. */
export function TreeSearch({ counts }: { counts: number[] }): ReactElement {
  const ui = treeUi(useEditorStore());
  const search = useStore(ui, (s) => s.search);
  const groups = useStore(ui, (s) => s.groups);
  const [legend, setLegend] = useState(false);
  return (
    <div style={{ padding: '4px 6px', borderBottom: '1px solid var(--border, #2a2e38)' }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input type="search" value={search} placeholder="Search nodes (g:3 l: r: d: s:)" aria-label="Search nodes" title={hint} spellCheck={false}
          style={{ flex: 1, font: 'inherit', background: 'var(--field, #101216)', color: 'inherit', border: '1px solid var(--border, #2a2e38)', minWidth: 0 }}
          onChange={(e) => { ui.getState().setSearch(e.target.value); }}
          onKeyDown={(e) => {
            if (e.code !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            ui.getState().clearFilters();
          }} />
        <button type="button" aria-label="Badge legend" aria-expanded={legend} title={badgeLegend}
          style={{ background: 'none', border: 'none', color: 'var(--muted, #8b919e)', cursor: 'pointer' }} onClick={() => { setLegend(!legend); }}>ⓘ</button>
      </div>
      <div style={{ display: 'flex', gap: 3, marginTop: 4, flexWrap: 'wrap' }}>
        {GROUPS.map((n) => (
          <button key={n} type="button" aria-pressed={groups.has(n)} title={`Group ${n} (Alt+${n})`} style={chip(groups.has(n), (counts[n - 1] ?? 0) === 0)}
            onClick={() => { ui.getState().toggleGroup(n); }}>{n} · {counts[n - 1] ?? 0}</button>
        ))}
      </div>
      {legend && <div role="note" style={{ marginTop: 4, fontSize: 11, color: 'var(--muted, #8b919e)', whiteSpace: 'normal' }}>{badgeLegend}</div>}
    </div>
  );
}
